<h2 id="my-time-off-request-dialog-title" mat-dialog-title tabindex="-1" cdkFocusInitial appLocalizationPopupDirective [localizationCode]="'TimeOffRequest'">
    {{ 'TimeOffRequest' | translate: {Default: "Time Off Request"} }}
</h2>

<mat-dialog-content aria-labelledby="my-time-off-request-dialog-title" aria-describedby="my-time-off-request-dialog-description">
    <p id="my-time-off-request-dialog-description" class="sr-only">
        Request details including status, approvers, dates, duration, and comments.
    </p>
    <div class="top">
        
        <div
            class="status"
            role="group"
            [attr.aria-label]="'Request status details. Status: ' + (timeOffRequest.requestStatus?.name || 'Unknown')"
        >
            <span class="label" appLocalizationPopupDirective [localizationCode]="'Status'">
                {{ 'Status' | translate: {Default: "Status"} }}:
                <mat-chip 
                [ngClass]="{
                    'red-chip': timeOffRequest.requestStatus?.id === 'trs_Denied',
                    'green-chip': timeOffRequest.requestStatus?.id === 'trs_Approved',
                    'orange-chip': timeOffRequest.requestStatus?.id === 'trs_Pending'
                }"
                >{{ timeOffRequest.requestStatus?.name }}</mat-chip>
            </span>

            
            <div class="requested-on">
                <span class="label" appLocalizationPopupDirective [localizationCode]="'RequestedOn'">{{ 'RequestedOn' | translate: {Default: "Requested On"} }}:</span> {{ moment(timeOffRequest.requestedOn).format('lll') }}
            </div>

            <div class="status-updated-by" *ngIf="timeOffRequest.requestStatusSetBy !== null">
                <span class="label" appLocalizationPopupDirective [localizationCode]="'RequestStatusSetBy'">{{ 'RequestStatusSetBy' | translate: {Default: "Request Status Set By"} }}:</span>
                
                <div class="employee-name">
                    <app-employee-image [profilePhoto]="timeOffRequest.requestStatusSetBy?.profilePhoto" [width]="35" [height]="35" [round]="true" *ngIf="!isLoading"></app-employee-image>

                    <span class="employee">
                        {{timeOffRequest.requestStatusSetBy?.firstName}} {{timeOffRequest.requestStatusSetBy?.lastName}}
                    </span>
                </div>
            </div>

            <div class="status-date" *ngIf="timeOffRequest.requestStatusUpdatedOn !== null">
                <span class="label" appLocalizationPopupDirective [localizationCode]="'RequestStatusUpdatedOn'">{{ 'RequestStatusUpdatedOn' | translate: {Default: "Request Status Updated On"} }}:</span>

                {{ moment(timeOffRequest.requestStatusUpdatedOn).format('lll') }}
            </div>

            <div class="status-date" *ngIf="timeOffRequest.requestStatusComments">
                <div class="label" appLocalizationPopupDirective [localizationCode]="'StatusComments'">{{ 'StatusComments' | translate: {Default: "Status Comments "} }}: </div>
                {{timeOffRequest.requestStatusComments}}
            </div>
        </div>
        
        <div class="approval" role="group" aria-label="Approver details">
            <div
                class="approved-by"
                role="group"
                [attr.aria-label]="'Approver' + ((timeOffRequest?.managers?.length || 0) > 1 ? 's' : '') + ': ' + (showEmployeeTooltip(timeOffRequest?.managers) || 'Not assigned')"
            >
                <span class="label" appLocalizationPopupDirective [localizationCode]="'Approvers'">{{ 'Approvers' | translate: {Default: "Approvers"} }}:</span>

                <ng-container *ngIf="(timeOffRequest?.managers?.length || 0) > 1">
                    <mat-icon
                        class="material-icons-outlined employee-list-icon"
                        [matBadge]="timeOffRequest.managers.length"
                        [matTooltip]="showEmployeeTooltip(timeOffRequest.managers)"
                        [attr.aria-label]="'Approvers: ' + timeOffRequest.managers.length + '. ' + showEmployeeTooltip(timeOffRequest.managers)"
                    >
                        account_circle
                    </mat-icon>
                </ng-container>
    
                <ng-container *ngIf="(timeOffRequest?.managers?.length || 0) == 1">
                    <ng-container *ngFor="let employee of timeOffRequest.managers">
                        <div class="employee-name">
                            <app-employee-image [profilePhoto]="employee.profilePhoto" [width]="35" [height]="35" [round]="true" *ngIf="!isLoading"></app-employee-image>
    
                            <span class="employee">
                                {{employee.firstName}} {{employee.lastName}}
                            </span>
                        </div>
                    </ng-container>
                </ng-container>
            </div>
        </div>

    </div>
    <div class="bottom" role="group" aria-label="Request details">
        <div class="employee-name"><span class="sr-only">{{ 'RequestedBy' | translate: {Default: 'Requested by'} }}: </span>
            <app-employee-image [profilePhoto]="timeOffRequest.requestedBy?.profilePhoto" [width]="35" [height]="35" [round]="true"></app-employee-image>

            <p class="employee">
                {{timeOffRequest.requestedBy?.firstName}} {{timeOffRequest.requestedBy?.lastName}}
            </p>
        </div>

        <p><span class="sr-only">{{ 'TimeOffType' | translate: {Default: 'Time off type'} }}: </span>{{timeOffRequest.timeOffType?.name}}</p>

        <p class="dates"><span class="sr-only">{{ 'DateRange' | translate: {Default: 'Date range'} }}: </span>
            {{ moment(timeOffRequest.from).format('ll') }} - {{ moment(timeOffRequest.to).format('ll') }}
            <mat-chip class="green-chip" *ngIf="timeOffRequest.halfDay === true" appLocalizationPopupDirective [localizationCode]="'LeaveRequestHalfDay'">{{ 'LeaveRequestHalfDay' | translate: {Default: "Half Day"} }}</mat-chip>
        </p>

        <p *ngIf="timeOffRequest.startTime && timeOffRequest.endTime"><span class="sr-only">{{ 'TimeRange' | translate: {Default: 'Time range'} }}: </span>{{ formatTime(timeOffRequest.startTime) }} - {{ formatTime(timeOffRequest.endTime) }}</p>
        <p *ngIf="!loadingWorkingHours && workingHours" appLocalizationPopupDirective [localizationCode]="'WorkingHours'">{{workingHours}} {{ 'WorkingHours' | translate: {Default: "working hours"} }}</p>
        <p *ngIf="timeOffRequest.hours" appLocalizationPopupDirective [localizationCode]="'Hours'">{{ timeOffRequest.hours }} {{ 'Hours' | translate: {Default: "hours"} }}</p>
        
        <p class="comments"><span class="sr-only">{{ 'Comments' | translate: {Default: 'Comments'} }}: </span><span class="sr-only" *ngIf="!timeOffRequest.comments">{{ 'NoComments' | translate: {Default: 'No comments'} }}</span>
            {{timeOffRequest.comments}}
        </p>
    </div>
</mat-dialog-content>

<mat-dialog-actions>
    <button mat-stroked-button class="close" appPreventDoubleClick (throttledClick)="close()" type="button" aria-label="Close dialog">{{ 'Close' | translate: {Default: "Close"} }}</button>
</mat-dialog-actions>
